/**
 * Performance Monitoring and Alerting System
 * 
 * Tracks application performance metrics and alerts when issues are detected
 * Helps identify bottlenecks and performance degradation in real-time
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      pageLoads: [],
      apiCalls: [],
      transactions: [],
      errors: [],
      resourceUsage: []
    };

    this.maxHistorySize = 100;
    this.alertThresholds = {
      slowPageLoad: 3000, // 3 seconds
      slowApiCall: 2000, // 2 seconds
      slowTransaction: 30000, // 30 seconds
      errorRate: 10, // 10% error rate
      memoryUsage: 90 // 90% memory usage
    };

    this.alerts = [];
    this.listeners = new Set();

    // Start monitoring
    this.startMonitoring();
  }

  /**
   * Track page load performance
   */
  trackPageLoad(pageName, startTime = performance.now()) {
    const duration = performance.now() - startTime;
    
    const metric = {
      type: 'pageLoad',
      page: pageName,
      duration,
      timestamp: Date.now(),
      navigation: this.getNavigationTiming()
    };

    this.addMetric('pageLoads', metric);

    // Alert if slow
    if (duration > this.alertThresholds.slowPageLoad) {
      this.createAlert('warning', `Slow page load: ${pageName} (${duration.toFixed(0)}ms)`);
    }

    console.log(`📊 Page Load: ${pageName} - ${duration.toFixed(0)}ms`);
    return metric;
  }

  /**
   * Track API call performance
   */
  trackApiCall(endpoint, duration, success = true, errorMessage = null) {
    const metric = {
      type: 'apiCall',
      endpoint,
      duration,
      success,
      errorMessage,
      timestamp: Date.now()
    };

    this.addMetric('apiCalls', metric);

    if (!success) {
      this.addMetric('errors', {
        type: 'api',
        endpoint,
        message: errorMessage,
        timestamp: Date.now()
      });
    }

    // Alert if slow
    if (success && duration > this.alertThresholds.slowApiCall) {
      this.createAlert('warning', `Slow API call: ${endpoint} (${duration.toFixed(0)}ms)`);
    }

    return metric;
  }

  /**
   * Track transaction performance
   */
  trackTransaction(operation, duration, success = true, errorMessage = null) {
    const metric = {
      type: 'transaction',
      operation,
      duration,
      success,
      errorMessage,
      timestamp: Date.now()
    };

    this.addMetric('transactions', metric);

    if (!success) {
      this.addMetric('errors', {
        type: 'transaction',
        operation,
        message: errorMessage,
        timestamp: Date.now()
      });
    }

    // Alert if slow or failed
    if (success && duration > this.alertThresholds.slowTransaction) {
      this.createAlert('warning', `Slow transaction: ${operation} (${duration.toFixed(0)}ms)`);
    } else if (!success) {
      this.createAlert('error', `Transaction failed: ${operation} - ${errorMessage}`);
    }

    return metric;
  }

  /**
   * Track generic error
   */
  trackError(type, message, context = {}) {
    const error = {
      type,
      message,
      context,
      timestamp: Date.now(),
      stack: new Error().stack
    };

    this.addMetric('errors', error);

    // Check error rate
    this.checkErrorRate();

    return error;
  }

  /**
   * Add metric to history
   */
  addMetric(category, metric) {
    if (!this.metrics[category]) {
      this.metrics[category] = [];
    }

    this.metrics[category].unshift(metric);

    // Keep history manageable
    if (this.metrics[category].length > this.maxHistorySize) {
      this.metrics[category] = this.metrics[category].slice(0, this.maxHistorySize);
    }
  }

  /**
   * Create alert
   */
  createAlert(severity, message) {
    const alert = {
      severity, // 'info', 'warning', 'error', 'critical'
      message,
      timestamp: Date.now()
    };

    this.alerts.unshift(alert);
    
    // Keep only recent alerts
    if (this.alerts.length > 50) {
      this.alerts = this.alerts.slice(0, 50);
    }

    // Log to console
    const emoji = {
      info: 'ℹ️',
      warning: '⚠️',
      error: '❌',
      critical: '🚨'
    }[severity];

    console.log(`${emoji} Performance Alert [${severity}]: ${message}`);

    // Notify listeners
    this.notifyListeners(alert);

    return alert;
  }

  /**
   * Check error rate and alert if high
   */
  checkErrorRate() {
    const recentWindow = 60000; // Last 1 minute
    const now = Date.now();

    const recentErrors = this.metrics.errors.filter(
      e => now - e.timestamp < recentWindow
    ).length;

    const recentCalls = [
      ...this.metrics.apiCalls,
      ...this.metrics.transactions
    ].filter(m => now - m.timestamp < recentWindow).length;

    if (recentCalls > 10) { // Only check if we have enough data
      const errorRate = (recentErrors / recentCalls) * 100;

      if (errorRate > this.alertThresholds.errorRate) {
        this.createAlert(
          'error',
          `High error rate: ${errorRate.toFixed(1)}% (${recentErrors}/${recentCalls} in last minute)`
        );
      }
    }
  }

  /**
   * Get navigation timing from browser
   */
  getNavigationTiming() {
    if (!window.performance || !window.performance.timing) {
      return null;
    }

    const timing = window.performance.timing;
    return {
      dns: timing.domainLookupEnd - timing.domainLookupStart,
      tcp: timing.connectEnd - timing.connectStart,
      request: timing.responseStart - timing.requestStart,
      response: timing.responseEnd - timing.responseStart,
      dom: timing.domComplete - timing.domLoading,
      load: timing.loadEventEnd - timing.loadEventStart
    };
  }

  /**
   * Get current performance metrics
   */
  getMetrics() {
    const now = Date.now();
    const last5Min = 300000;

    // Filter recent metrics
    const recent = {
      pageLoads: this.metrics.pageLoads.filter(m => now - m.timestamp < last5Min),
      apiCalls: this.metrics.apiCalls.filter(m => now - m.timestamp < last5Min),
      transactions: this.metrics.transactions.filter(m => now - m.timestamp < last5Min),
      errors: this.metrics.errors.filter(m => now - m.timestamp < last5Min)
    };

    // Calculate averages
    const avgPageLoad = this.calculateAverage(recent.pageLoads, 'duration');
    const avgApiCall = this.calculateAverage(recent.apiCalls, 'duration');
    const avgTransaction = this.calculateAverage(recent.transactions, 'duration');

    // Calculate success rates
    const apiSuccessRate = this.calculateSuccessRate(recent.apiCalls);
    const transactionSuccessRate = this.calculateSuccessRate(recent.transactions);

    return {
      summary: {
        avgPageLoad,
        avgApiCall,
        avgTransaction,
        apiSuccessRate,
        transactionSuccessRate,
        errorCount: recent.errors.length,
        totalErrors: this.metrics.errors.length
      },
      recent,
      alerts: this.alerts.slice(0, 10) // Last 10 alerts
    };
  }

  /**
   * Calculate average of a metric
   */
  calculateAverage(metrics, field) {
    if (metrics.length === 0) return 0;
    const sum = metrics.reduce((acc, m) => acc + (m[field] || 0), 0);
    return Math.round(sum / metrics.length);
  }

  /**
   * Calculate success rate
   */
  calculateSuccessRate(metrics) {
    if (metrics.length === 0) return 100;
    const successful = metrics.filter(m => m.success).length;
    return ((successful / metrics.length) * 100).toFixed(1);
  }

  /**
   * Start monitoring resource usage
   */
  startMonitoring() {
    // Monitor memory usage periodically
    setInterval(() => {
      if (window.performance && window.performance.memory) {
        const memory = window.performance.memory;
        const usagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;

        this.addMetric('resourceUsage', {
          type: 'memory',
          used: memory.usedJSHeapSize,
          total: memory.jsHeapSizeLimit,
          percent: usagePercent,
          timestamp: Date.now()
        });

        // Alert if high memory usage
        if (usagePercent > this.alertThresholds.memoryUsage) {
          this.createAlert(
            'warning',
            `High memory usage: ${usagePercent.toFixed(1)}%`
          );
        }
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Subscribe to alerts
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of new alert
   */
  notifyListeners(alert) {
    for (const callback of this.listeners) {
      try {
        callback(alert);
      } catch (error) {
        console.error('Error in alert listener:', error);
      }
    }
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics = {
      pageLoads: [],
      apiCalls: [],
      transactions: [],
      errors: [],
      resourceUsage: []
    };
    this.alerts = [];
    console.log('🗑️ Cleared all performance metrics');
  }

  /**
   * Generate performance report
   */
  generateReport() {
    const metrics = this.getMetrics();
    
    return {
      timestamp: new Date().toISOString(),
      summary: metrics.summary,
      recommendations: this.generateRecommendations(metrics.summary),
      recentAlerts: metrics.alerts,
      healthScore: this.calculateHealthScore(metrics.summary)
    };
  }

  /**
   * Calculate health score (0-100)
   */
  calculateHealthScore(summary) {
    let score = 100;

    // Deduct points for slow performance
    if (summary.avgPageLoad > this.alertThresholds.slowPageLoad) {
      score -= 20;
    } else if (summary.avgPageLoad > this.alertThresholds.slowPageLoad * 0.7) {
      score -= 10;
    }

    if (summary.avgApiCall > this.alertThresholds.slowApiCall) {
      score -= 15;
    }

    // Deduct points for errors
    if (summary.apiSuccessRate < 95) {
      score -= 20;
    } else if (summary.apiSuccessRate < 98) {
      score -= 10;
    }

    if (summary.transactionSuccessRate < 90) {
      score -= 25;
    } else if (summary.transactionSuccessRate < 95) {
      score -= 15;
    }

    return Math.max(0, score);
  }

  /**
   * Generate performance recommendations
   */
  generateRecommendations(summary) {
    const recommendations = [];

    if (summary.avgPageLoad > this.alertThresholds.slowPageLoad) {
      recommendations.push('Page loads are slow. Consider implementing code splitting or lazy loading.');
    }

    if (summary.avgApiCall > this.alertThresholds.slowApiCall) {
      recommendations.push('API calls are slow. Review database queries and add caching.');
    }

    if (summary.apiSuccessRate < 98) {
      recommendations.push('API success rate is low. Check error logs and improve error handling.');
    }

    if (summary.transactionSuccessRate < 95) {
      recommendations.push('Transaction success rate is low. Review blockchain connection and transaction logic.');
    }

    if (summary.errorCount > 10) {
      recommendations.push(`High error count (${summary.errorCount}). Review recent errors and fix critical issues.`);
    }

    if (recommendations.length === 0) {
      recommendations.push('Performance is good! No major issues detected.');
    }

    return recommendations;
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;

/**
 * Helper functions for easy tracking
 */

export function trackPageLoad(pageName) {
  return performanceMonitor.trackPageLoad(pageName);
}

export function trackApiCall(endpoint, startTime) {
  const duration = Date.now() - startTime;
  return performanceMonitor.trackApiCall(endpoint, duration, true);
}

export function trackApiError(endpoint, startTime, error) {
  const duration = Date.now() - startTime;
  return performanceMonitor.trackApiCall(endpoint, duration, false, error.message);
}

export function trackTransaction(operation, startTime, success, errorMessage = null) {
  const duration = Date.now() - startTime;
  return performanceMonitor.trackTransaction(operation, duration, success, errorMessage);
}

export function trackError(type, message, context) {
  return performanceMonitor.trackError(type, message, context);
}

export function getPerformanceMetrics() {
  return performanceMonitor.getMetrics();
}

export function getPerformanceReport() {
  return performanceMonitor.generateReport();
}

export function subscribeToAlerts(callback) {
  return performanceMonitor.subscribe(callback);
}

/**
 * Higher-order function to wrap async functions with performance tracking
 */
export function withPerformanceTracking(name, fn, type = 'apiCall') {
  return async (...args) => {
    const startTime = Date.now();
    
    try {
      const result = await fn(...args);
      
      if (type === 'apiCall') {
        trackApiCall(name, startTime);
      } else if (type === 'transaction') {
        trackTransaction(name, startTime, true);
      }
      
      return result;
    } catch (error) {
      if (type === 'apiCall') {
        trackApiError(name, startTime, error);
      } else if (type === 'transaction') {
        trackTransaction(name, startTime, false, error.message);
      }
      
      throw error;
    }
  };
}

